import { apiClient } from '@/services/api';
import type { MeStats } from '@/types/earning';
import type { Sport } from '@/types/user';

export interface ProfilePatch {
  first_name?: string;
  last_name?: string;
  email?: string;
  school?: string;
  sport?: Sport;
  push_enabled?: boolean;
}

export const usersService = {
  /**
   * PATCH /api/v1/users/me/profile — partial update of the user record.
   * Server returns `{ user }` with the nested-school payload (Phase 6 shape);
   * onboarding only cares that the call succeeds, then triggers
   * `authStore.refreshMe()` to re-pull the lean auth#me view.
   */
  async updateProfile(patch: ProfilePatch): Promise<void> {
    await apiClient.patch('/api/v1/users/me/profile', patch);
  },

  /**
   * Profile-tab email change (same endpoint). Returns the address as the
   * server saved it (normalized), so the screen can show it even if the
   * follow-up refresh fails: a retry would spend one of the user's 3 weekly
   * email changes on a no-op.
   */
  async changeEmail(email: string): Promise<string> {
    const res = await apiClient.patch<{ user: { email: string } }>('/api/v1/users/me/profile', { email });
    return res.user.email;
  },

  /**
   * Flip the master push opt-out. Thin wrapper over updateProfile so the
   * settings toggle reads clearly at the call site.
   */
  async setPushEnabled(enabled: boolean): Promise<void> {
    await apiClient.patch('/api/v1/users/me/profile', { push_enabled: enabled });
  },

  /**
   * GET /api/v1/users/me — full user payload including the three money
   * stats (balance, lifetime, this-month). The earnings tab is the
   * only consumer for now and only needs the totals, so we pluck just
   * those into MeStats. Auth.store keeps using auth#me for the lean
   * identity shape and stays the source of truth for `user`.
   */
  async stats(): Promise<MeStats> {
    const { user } = await apiClient.get<{
      user: {
        balance_cents: number;
        lifetime_earned_cents: number;
        month_earnings_cents: number;
        school: { name: string } | null;
      };
    }>('/api/v1/users/me');
    return {
      balance_cents: user.balance_cents,
      lifetime_earned_cents: user.lifetime_earned_cents,
      month_earnings_cents: user.month_earnings_cents,
      school_name: user.school?.name ?? null,
    };
  },
};
